/**
 * Tracker consent policy. Pure and isomorphic: the browser, the server and the
 * tests all decide telemetry through `shouldLoadTelemetry`.
 *
 * Categories:
 * - `necessary`   sign-in, security and the consent cookie itself. Always on.
 * - `analytics`   PostHog product analytics, Vercel Analytics, Speed Insights.
 * - `diagnostics` Axiom browser operational telemetry (errors, request timing).
 *
 * Defaults, when the person has made no current-version choice:
 * - EU/EEA/UK and Swiss visitors (`optInRegion`): everything except
 *   `necessary` is opt-in. The banner asks, and nothing runs until they answer (ePrivacy
 *   Art 5(3) and GDPR consent; a pre-ticked box is not consent).
 * - Everyone else: `analytics` is on and can be turned off at any time in
 *   Settings or Privacy choices; `diagnostics` stays opt-in.
 * An explicit stored choice wins in both directions. A Global Privacy Control
 * or Do Not Track signal, or a HIPAA-mode account, keeps every category off
 * regardless of stored choices or defaults.
 *
 * Provenance: a saved "analytics on" that the person never chose (they saved
 * the preferences dialog without touching the analytics switch while the
 * regional default showed it on) is stored on the device with
 * `analyticsBasis: "default"`. It is not a decision: it keeps following the
 * regional default, so it is "off, ask" in the opt-in region, and it never
 * becomes the account copy. The marker lives in the device cookie and the
 * consent receipt only; the account copy in the silo holds affirmative choices.
 *
 * Ownership: the cookie belongs to the browser, not to a person, and it
 * outlives sign-out. Once a choice reaches an account (synced, or saved while
 * signed in) the device copy carries that account's opaque `owner` tag (a
 * server-derived hash, never the account id). On a signed-in device a choice
 * tagged for another account is no choice at all: it never reaches this
 * account and never decides this account's telemetry (`deviceConsentForOwner`).
 * An untagged choice was made signed out (or predates the tag); the first
 * account that reconciles takes it as its own and tags it.
 */

import { countryFromLocale, countryFromTimeZone, isEuUkCountry } from "@/src/shared/region/euUkCountry";

export const CONSENT_COOKIE_NAME = "sm_consent";
/** Bump when a category's meaning changes; stored choices of an older version are ignored. */
export const CONSENT_POLICY_VERSION = 1;
/** How long a choice is remembered on a device before the banner asks again. */
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

export type ConsentCategory = "necessary" | "analytics" | "diagnostics";
export type TelemetryCategory = Exclude<ConsentCategory, "necessary">;
export const TELEMETRY_CATEGORIES: readonly TelemetryCategory[] = ["analytics", "diagnostics"];

export type ConsentChoices = Readonly<{
  analytics: boolean;
  diagnostics: boolean;
}>;

export type ConsentState = ConsentChoices & Readonly<{
  version: number;
  /** Epoch ms when the person made (or the account restored) this choice. */
  decidedAt: number;
  /** A privacy signal was present when the choice was recorded. */
  gpc?: boolean;
}>;

/**
 * The device copy. `analyticsBasis: "default"` marks an "analytics on" the
 * person did not affirm (see the module comment). Only the device cookie and
 * the consent receipt carry it; `normalizeConsentState` strips it.
 * `owner` is the opaque tag of the account this choice belongs to; absent
 * while the choice is anonymous. Only the device cookie carries it.
 */
export type DeviceConsentState = ConsentState & Readonly<{
  analyticsBasis?: "default";
  owner?: string;
}>;

/** Shape of an account owner tag: base64url, no account id or other PII. */
const CONSENT_OWNER_TAG = /^[A-Za-z0-9_-]{16,64}$/;

/** Strict parse of an untrusted owner tag (server response or cookie). */
export function normalizeConsentOwnerTag(value: unknown): string | null {
  return typeof value === "string" && CONSENT_OWNER_TAG.test(value) ? value : null;
}

/** The device copy bound to an account. A null owner returns the copy untagged. */
export function withConsentOwner(state: DeviceConsentState, owner: string | null): DeviceConsentState {
  const untagged: DeviceConsentState = {
    version: state.version,
    analytics: state.analytics,
    diagnostics: state.diagnostics,
    decidedAt: state.decidedAt,
    ...(state.gpc ? { gpc: true } : {}),
    ...(state.analyticsBasis === "default" ? { analyticsBasis: "default" as const } : {}),
  };
  return owner ? { ...untagged, owner } : untagged;
}

/**
 * The device copy as seen by a signed-in account. A choice tagged for another
 * account (or for some account while this one is not yet known) is not this
 * account's decision, so it reads as no choice. Untagged copies are anonymous
 * and count for whoever is signed in.
 */
export function deviceConsentForOwner(
  consent: DeviceConsentState | null | undefined,
  owner: string | null,
): DeviceConsentState | null {
  if (!consent) return null;
  if (consent.owner === undefined) return consent;
  return owner !== null && consent.owner === owner ? consent : null;
}

/** Compliance posture that overrides consent. `"hipaa"` denies all telemetry. */
export type TelemetryComplianceMode = "standard" | "hipaa";

/** The stored choice when it was made under the current policy version, else null. */
export function currentConsent<T extends ConsentState>(consent: T | null | undefined): T | null {
  return consent && consent.version === CONSENT_POLICY_VERSION ? consent : null;
}

/** True when the stored "analytics on" was never affirmed and follows the regional default. */
export function isDefaultAnalyticsBasis(consent: DeviceConsentState | null | undefined): boolean {
  return currentConsent(consent)?.analyticsBasis === "default";
}

/**
 * Timezones that say nothing about where the device is: privacy-hardened
 * browsers report UTC on purpose. They count as "maybe EU/UK".
 */
function isUninformativeTimeZone(timeZone: string): boolean {
  return /^(?:Etc\/)?(?:UTC|UCT|GMT|Universal|Zulu|Greenwich)(?:[+-]0)?$/i.test(timeZone)
    || /^Etc\//i.test(timeZone);
}

/**
 * Switzerland is not in the shared EU/UK rule, but the privacy policy names
 * consent as the legal basis for analytics there too, so its visitors are
 * asked first like EU/UK visitors.
 */
const SWISS_COUNTRY = "CH";
const SWISS_TIME_ZONE = "Europe/Zurich";

function isConsentOptInCountry(country: string | null): boolean {
  return country === SWISS_COUNTRY || isEuUkCountry(country);
}

/**
 * True when tracker consent must be opt-in for this device: any EU/EEA/UK or
 * Swiss signal in the browser timezone or a preferred language, or no usable
 * timezone at all. It fails closed toward opt-in, so a false positive only
 * shows the banner; it never runs analytics without asking someone in those
 * regions. Uses the shared EU/UK country rule (`src/shared/region`).
 */
export function isConsentOptInRegion({
  timeZone,
  languages,
}: {
  timeZone: string | null | undefined;
  languages?: readonly (string | null | undefined)[] | null;
}): boolean {
  const zone = typeof timeZone === "string" ? timeZone.trim() : "";
  if (!zone || isUninformativeTimeZone(zone)) return true;
  if (zone === SWISS_TIME_ZONE || countryFromTimeZone(zone)) return true;
  return (languages ?? []).some((language) => isConsentOptInCountry(countryFromLocale(language)));
}

/** What applies before any choice: analytics on outside the opt-in region, everything else off. */
export function defaultConsentChoices(optInRegion: boolean): ConsentChoices {
  return { analytics: !optInRegion, diagnostics: false };
}

export type ConsentDefaultsOptions = {
  /**
   * False while the regional default must not apply yet: a signed-in device
   * whose account compliance mode or account copy has not been read. Only an
   * explicit stored choice counts then; everything else is off.
   */
  defaultsApply?: boolean;
};

/**
 * The current-version stored choice, or the regional default when there is
 * none. An unaffirmed "analytics on" follows the regional default too.
 * Privacy signals and HIPAA mode are applied by `shouldLoadTelemetry`.
 */
export function effectiveConsentChoices(
  consent: DeviceConsentState | null | undefined,
  optInRegion: boolean,
  { defaultsApply = true }: ConsentDefaultsOptions = {},
): ConsentChoices {
  const current = currentConsent(consent);
  const regional = defaultsApply ? defaultConsentChoices(optInRegion) : { analytics: false, diagnostics: false };
  if (!current) return regional;
  return {
    analytics: current.analyticsBasis === "default" ? regional.analytics : current.analytics,
    diagnostics: current.diagnostics,
  };
}

export function shouldLoadTelemetry({
  category,
  consent,
  gpc,
  complianceMode,
  optInRegion,
  defaultsApply = true,
}: {
  category: ConsentCategory;
  consent: DeviceConsentState | null | undefined;
  gpc: boolean;
  complianceMode?: TelemetryComplianceMode | null;
  /** From `isConsentOptInRegion`. Decides the default when there is no stored choice. */
  optInRegion: boolean;
} & ConsentDefaultsOptions): boolean {
  if (category === "necessary") return true;
  if (complianceMode === "hipaa") return false;
  if (gpc) return false;
  return effectiveConsentChoices(consent, optInRegion, { defaultsApply })[category];
}

/**
 * True when the banner must ask: an opt-in (EU/UK) device with no
 * current-version decision and no privacy signal. An unaffirmed "analytics
 * on" (saved elsewhere, then carried here) is no decision either. Elsewhere
 * the default applies and the person changes it in Settings or Privacy choices.
 */
export function needsConsentDecision(
  consent: DeviceConsentState | null | undefined,
  { gpc, optInRegion }: { gpc: boolean; optInRegion: boolean },
): boolean {
  if (gpc || !optInRegion) return false;
  const current = currentConsent(consent);
  return current === null || current.analyticsBasis === "default";
}

/**
 * Builds a state from choices. `analyticsAffirmed: false` means the person did
 * not set the analytics switch themselves; an "on" is then stored with
 * `analyticsBasis: "default"` so it never counts as consent.
 */
export function createConsentState(
  choices: ConsentChoices,
  {
    now = Date.now(),
    gpc = false,
    analyticsAffirmed = true,
  }: { now?: number; gpc?: boolean; analyticsAffirmed?: boolean } = {},
): DeviceConsentState {
  // A privacy signal wins over any stored "on", so the record matches what runs.
  const analytics = choices.analytics === true && !gpc;
  return {
    version: CONSENT_POLICY_VERSION,
    analytics,
    diagnostics: choices.diagnostics === true && !gpc,
    decidedAt: Math.floor(now),
    ...(gpc ? { gpc: true } : {}),
    ...(analytics && !analyticsAffirmed ? { analyticsBasis: "default" as const } : {}),
  };
}

/**
 * Strict parse of an untrusted value (cookie JSON, request body, stored
 * preference). Rebuilds the account shape, so a device-only `analyticsBasis`
 * marker is dropped; read device cookies with `normalizeDeviceConsentState`.
 */
export function normalizeConsentState(value: unknown): ConsentState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const version = candidate.version;
  const decidedAt = candidate.decidedAt;
  if (typeof version !== "number" || !Number.isSafeInteger(version) || version < 1 || version > 1_000) return null;
  if (typeof decidedAt !== "number" || !Number.isSafeInteger(decidedAt) || decidedAt <= 0) return null;
  if (typeof candidate.analytics !== "boolean" || typeof candidate.diagnostics !== "boolean") return null;
  if (candidate.gpc !== undefined && typeof candidate.gpc !== "boolean") return null;
  return {
    version,
    analytics: candidate.analytics,
    diagnostics: candidate.diagnostics,
    decidedAt,
    ...(candidate.gpc === true ? { gpc: true } : {}),
  };
}

/**
 * Strict parse of a device copy: the account shape plus the optional
 * `analyticsBasis` marker and `owner` tag. Cookies written before the tag
 * existed parse as untagged (anonymous) choices.
 */
export function normalizeDeviceConsentState(value: unknown): DeviceConsentState | null {
  const state = normalizeConsentState(value);
  if (!state) return null;
  const candidate = value as Record<string, unknown>;
  const basis = candidate.analyticsBasis;
  // The marker only qualifies an "on"; anything else is a malformed cookie.
  if (basis !== undefined && (basis !== "default" || !state.analytics)) return null;
  // A malformed tag cannot be attributed to anyone, so the cookie is dropped.
  const owner = candidate.owner === undefined ? undefined : normalizeConsentOwnerTag(candidate.owner);
  if (owner === null) return null;
  return {
    ...state,
    ...(basis === "default" ? { analyticsBasis: "default" as const } : {}),
    ...(owner ? { owner } : {}),
  };
}

/**
 * Cookie value: versioned JSON with the decision timestamp, URI-encoded so it is
 * a valid cookie octet string, e.g. `{"version":1,"analytics":false,...}`.
 */
export function encodeConsentCookie(state: DeviceConsentState): string {
  return encodeURIComponent(JSON.stringify({
    version: state.version,
    analytics: state.analytics,
    diagnostics: state.diagnostics,
    decidedAt: state.decidedAt,
    ...(state.gpc ? { gpc: true } : {}),
    ...(state.analyticsBasis === "default" && state.analytics ? { analyticsBasis: "default" } : {}),
    ...(normalizeConsentOwnerTag(state.owner) ? { owner: state.owner } : {}),
  }));
}

export function parseConsentCookie(raw: string | null | undefined): DeviceConsentState | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value || value.length > 512) return null;
  try {
    return normalizeDeviceConsentState(JSON.parse(decodeURIComponent(value)));
  } catch {
    return null;
  }
}

/** Reads `sm_consent` out of a `Cookie` header or `document.cookie` string. */
export function readConsentFromCookieHeader(cookieHeader: string | null | undefined): DeviceConsentState | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(";")) {
    const separator = part.indexOf("=");
    if (separator < 0) continue;
    if (part.slice(0, separator).trim() !== CONSENT_COOKIE_NAME) continue;
    return parseConsentCookie(part.slice(separator + 1));
  }
  return null;
}

/** `Sec-GPC: 1` (Global Privacy Control) or `DNT: 1` on a request. */
export function hasRequestPrivacySignal(headers: Pick<Headers, "get">): boolean {
  const flag = (name: string) => headers.get(name)?.trim() === "1";
  return flag("sec-gpc") || flag("dnt");
}

export type BrowserPrivacySignalSource = {
  doNotTrack?: string | null;
  globalPrivacyControl?: boolean;
  msDoNotTrack?: string | null;
};

/** `navigator.globalPrivacyControl` or a Do Not Track signal in the browser. */
export function hasBrowserPrivacySignal(navigatorLike: BrowserPrivacySignalSource | null | undefined): boolean {
  if (!navigatorLike) return false;
  const on = (value: string | null | undefined) => {
    const normalized = value?.trim().toLowerCase();
    return normalized === "1" || normalized === "yes";
  };
  return navigatorLike.globalPrivacyControl === true
    || on(navigatorLike.doNotTrack)
    || on(navigatorLike.msDoNotTrack);
}

/**
 * Picks the newer of a device choice and an account choice. Used when a signed-in
 * device syncs with the account: the latest explicit decision wins everywhere.
 */
export function newerConsent(
  device: ConsentState | null,
  account: ConsentState | null,
): ConsentState | null {
  const a = currentConsent(device);
  const b = currentConsent(account);
  if (!a) return b;
  if (!b) return a;
  return b.decidedAt > a.decidedAt ? b : a;
}

export type ConsentSyncPlan = Readonly<{
  /** Store on this device (account copy, a merge of both, or the device copy now tagged). */
  adopt: DeviceConsentState | null;
  /**
   * Write to the account. A device copy with an unaffirmed "analytics on" is
   * sent with its basis, and the server stores it with `analytics: false`.
   */
  push: DeviceConsentState | null;
}>;

/**
 * The account copy the server keeps for a device default: its diagnostics
 * choice with analytics off, at the same `decidedAt` (see the consent route).
 */
function isAccountCopyOfDefault(local: DeviceConsentState, remote: ConsentState): boolean {
  return !remote.analytics && remote.decidedAt === local.decidedAt && remote.diagnostics === local.diagnostics;
}

/**
 * Signed-in device and account sync. Between two decisions the newer one wins
 * everywhere. A device copy with an unaffirmed "analytics on" holds no
 * analytics decision: an account decision supplies its analytics choice while
 * the newer of the two supplies diagnostics. With no account copy, its
 * diagnostics-on choice is pushed so it reaches the account; the account
 * stores it with analytics off, and this device keeps its default.
 *
 * `owner` is the signed-in account's tag. A device copy tagged for another
 * account is never pushed and never merged: the plan is the account copy
 * alone, as if the device had no choice. Everything adopted is tagged for
 * `owner`, and an untagged device copy is tagged once it is reconciled, so it
 * can never reach a second account.
 */
export function planConsentSync(
  device: DeviceConsentState | null,
  account: ConsentState | null,
  owner: string | null,
): ConsentSyncPlan {
  const own = deviceConsentForOwner(device, owner);
  const plan = planOwnedConsentSync(own, account);
  // An untagged device copy that stays as it is still becomes this account's.
  const tagOnly = owner !== null && own && currentConsent(own) && own.owner !== owner ? own : null;
  const adopt = plan.adopt ?? tagOnly;
  return { adopt: adopt ? withConsentOwner(adopt, owner) : null, push: plan.push };
}

function planOwnedConsentSync(
  device: DeviceConsentState | null,
  account: ConsentState | null,
): ConsentSyncPlan {
  const local = currentConsent(device);
  const remote = currentConsent(account);
  const none: ConsentSyncPlan = { adopt: null, push: null };
  if (!remote) {
    if (!local) return none;
    return local.analyticsBasis !== "default" || local.diagnostics ? { adopt: null, push: local } : none;
  }
  if (!local) return { adopt: remote, push: null };
  if (local.analyticsBasis === "default") {
    if (isAccountCopyOfDefault(local, remote)) return none;
    if (local.decidedAt > remote.decidedAt && local.diagnostics !== remote.diagnostics) {
      const merged = createConsentState(
        { analytics: remote.analytics, diagnostics: local.diagnostics },
        { now: local.decidedAt, gpc: remote.gpc === true },
      );
      return { adopt: merged, push: merged };
    }
    return { adopt: remote, push: null };
  }
  if (remote.decidedAt > local.decidedAt) return { adopt: remote, push: null };
  if (local.decidedAt > remote.decidedAt) return { adopt: null, push: local };
  return none;
}
